<template>
  <!-- position guard -->
  <div>
    <section
      :class="[
        $style.item,
        {
          [$style['item--active']]: active,
          [$style['item--checked']]: checked,
        },
      ]"
    >
      <div :class="$style.item__checkbox" v-if="checkable">
        <DolphinCheckbox
          :model-value="checked"
          @update:model-value="checked => emit('changeCheck', checked)"
          @click.stop
        />
      </div>
      <div :class="$style.item__content">
        <header :class="$style.header">
          <div :class="[$style.header__status, $style.status]">
            <ChatRoomStatusCircle
              :status="match.chatRoomStatus || ChatRoomStatus.NotDefined"
            />
            <p :class="$style.status__text">{{ statusString }}</p>
          </div>
          <div :class="$style.state">
            <FadeTransition>
              <span v-if="stateMessage" :class="$style.stateMessage"
                >{{ stateMessage }}
              </span>
            </FadeTransition>
            <time
              v-if="lastUpdatedTimeAgo"
              :datetime="lastUpdatedString"
              :class="[$style.state__time, $style.time]"
              >{{ lastUpdatedTimeAgo }}</time
            >
          </div>
        </header>
        <div :class="$style.middle">
          <div :class="[$style.nickname, $style['inner--nickname']]">
            {{ userNickname }}
          </div>
          <div :class="$style.memo" v-if="isExistMemo">
            <IconMemo />
            <div :class="$style.memoCount">{{ memoCount }}</div>
          </div>
        </div>
        <div :class="$style.footer">
          <div :class="$style.brief">
            {{ userBrief }}
          </div>
          <div
            :class="$style.normalMessageChatReplyStatus"
            v-if="normalMessageChatReplyDisabled"
          >
            フリーワード自動応答停止中
          </div>
        </div>
        <div
          :class="[$style.planner, $style['inner--planner']]"
          v-if="showPlanner"
        >
          <PlannerThumbnail
            :class="$style.planner__image"
            :image="match.planner.avatarImage"
            :width="48"
            :height="48"
          />
          <p :class="$style.planner__name">{{ match.planner.name }}</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import FadeTransition from '@/components/transitions/FadeTransition.vue'
import PlannerThumbnail from '@/components/atoms/PlannerThumbnail.vue'
import { formatUserProfile } from '@/libs/utilities/formatUserProfile'
import type { MatchEntrance } from '@/types/app'
import { useTimeAgo } from '@/hooks/useTimeAgo'
import ChatRoomStatusCircle from '@/components/molecules/ChatRoomStatusCircle.vue'
import { ChatRoomStatus } from '@/types/schema.generated'
import { chatRoomStatusString } from '@/models/chatRoom/chatRoomStatus'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'
import IconMemo from '@/components/icons/IconMemo.vue'

const props = withDefaults(
  defineProps<{
    checked: boolean
    match: MatchEntrance
    active?: boolean
    showPlanner?: boolean
    checkable: boolean
  }>(),
  {
    active: false,
    showPlanner: false,
  }
)

const emit = defineEmits<{
  (event: 'changeCheck', checked: boolean): void
}>()

const lastUpdated = computed(() => props.match.latestMessage?.data.createdAt)
const lastUpdatedString = computed(() => {
  return lastUpdated.value ? lastUpdated.value.toUTCString() : ''
})
const lastUpdatedTimeAgo = useTimeAgo(lastUpdated)
const userNickname = computed(() => props.match.user.nickname || 'ゲスト')
const userBrief = computed(() => formatUserProfile(props.match.user))
const normalMessageChatReplyDisabled = computed(() => {
  const disabledUntil =
    props.match.normalMessageChatReplyMatchSetting.disabledUntil
  if (!disabledUntil) {
    return false
  }
  return Date.now() < disabledUntil.getTime()
})

const memoCount = computed<number>(() => props.match.memos?.length ?? 0)

const isExistMemo = computed<boolean>(() => {
  return memoCount.value > 0
})

const stateMessage = computed<string | undefined>(() => {
  const hasUnreadMessage = !!props.match.latestMessage?.isUnread
  if (hasUnreadMessage) {
    return '未読'
  }

  const hasUnrepliedMessage = !!props.match.hasUnrepliedMessage
  if (hasUnrepliedMessage) {
    return '未返信'
  }

  return undefined
})

const statusString = computed<string>(() => {
  if (props.match.chatRoomStatus) {
    return chatRoomStatusString[props.match.chatRoomStatus]
  }
  return chatRoomStatusString[ChatRoomStatus.NotDefined]
})
</script>

<style lang="scss" module>
$item-x-padding: 12px;

.item {
  $root: &;

  position: relative;
  display: flex;
  min-height: 72px;
  box-sizing: border-box;
  gap: 16px;
  padding: 8px $item-x-padding 12px;
  background-color: $color-surface-primary;
  cursor: pointer;
  transition: background-color $transition-interaction;

  &:hover,
  &--active,
  &--checked {
    background-color: $color-surface-accent-primary-light;
  }

  &--active {
    cursor: auto;
  }

  &__content {
    flex: 1;
  }

  &__checkbox {
    display: flex;
    align-items: center;
  }

  &__profile {
    flex: 1;
    padding: 0 8px;

    @at-root #{$root}--archived & {
      filter: grayscale(100%);
      opacity: 0.7;
      transition: filter $transition-interaction;
    }

    @at-root #{$root}--archived:hover & {
      filter: none;
    }

    @at-root #{$root}--archived#{$root}--active & {
      filter: none;
      opacity: initial;
    }
  }
}

.inner {
  &--nickname {
    margin-top: 4px;
  }

  &--planner {
    margin-top: 4px;
  }
}

.header {
  display: flex;
  flex: 1;
  justify-content: space-between;
}

.status {
  display: flex;
  flex: 1;
  align-items: center;

  &__text {
    font-size: 12px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.brief {
  color: $color-text-and-object-medium-emphasis;
  font-size: 12px;
  white-space: nowrap;
}

.normalMessageChatReplyStatus {
  color: $color-text-and-object-accent-red;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
}

.middle {
  padding-bottom: 2px;
  display: flex;
  flex: 1;
  justify-content: space-between;
}

.nickname {
  max-width: 150px;
  color: $color-text-and-object-high-emphasis;
  font-size: 14px;
  font-weight: bold;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.memo {
  color: white;
  display: flex;
  align-items: center;
  font-size: 12px;
  flex: 0 0 12;
  background-color: $color-text-and-object-accent-primary;
  border-radius: 11px;
  margin-top: 6px;
  padding: 0 10px 0 6px;
}

.memoCount {
  padding-left: 4px;
  font-size: 10px;
  font-weight: bold;
}

.time {
  color: $color-text-and-object-low-emphasis;
  font-size: 11px;
}

.state {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.stateMessage {
  display: inline-block;
  padding: 0 8px;
  background-color: $color-label-red;
  border-radius: 9px;
  color: $color-text-and-object-high-emphasis-inverse;
  font-size: 10px;
  font-weight: bold;
  line-height: 18px;
}

.planner {
  display: flex;
  align-items: center;

  &__image {
    $size: 24px;

    width: $size;
    height: $size;
  }

  &__name {
    color: $color-text-and-object-high-emphasis;
    font-size: 10px;
    font-weight: normal;
    line-height: 12px;

    &:nth-child(n + 2) {
      margin-left: 8px;
    }
  }
}

.footer {
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
}
</style>
